import { useState, useRef, useEffect, useMemo } from 'react';
import { Bell, Menu, Gift, Users, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useWedding } from '../../context/WeddingContext';
import { getInitials, formatCurrency } from '../../lib/utils';

const SEEN_KEY = 'mdc_notif_seen_at';

function tsOf(value) {
  if (!value) return 0;
  if (typeof value === 'object' && typeof value.seconds === 'number') return value.seconds * 1000;
  const p = Date.parse(value);
  return Number.isNaN(p) ? 0 : p;
}

export function Topbar({ onMenuClick }) {
  const { user } = useAuth();
  const { wedding } = useWedding();
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useState(() => {
    const v = Number(localStorage.getItem(SEEN_KEY));
    return Number.isFinite(v) ? v : 0;
  });
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const notifications = useMemo(() => {
    const list = [];
    (wedding?.contributions || []).forEach((c) => {
      if (c.status && c.status !== 'paid') return;
      const gifts = Array.isArray(c.items) && c.items.length
        ? c.items.map((it) => it?.cardTitle).filter(Boolean)
        : (c.cardTitle ? [c.cardTitle] : []);
      list.push({
        id: `g-${c.purchaseId || c.id}`,
        type: 'gift',
        name: c.isAnonymous ? 'Convidado(a) anônimo(a)' : (c.guestName || 'Convidado'),
        detail: gifts.length ? gifts.join(', ') : 'um presente',
        value: Number(c.value || 0),
        ts: tsOf(c.createdAt) || tsOf(c.date ? `${c.date}T12:00:00` : null),
      });
    });
    (wedding?.rsvps || []).forEach((r) => {
      list.push({
        id: `r-${r.id || r.phone || r.name}`,
        type: 'rsvp',
        name: r.name || 'Convidado',
        detail: r.willAttend ? 'confirmou presença' : 'não poderá comparecer',
        ts: tsOf(r.confirmedAt) || tsOf(r.createdAt) || tsOf(r.updatedAt),
      });
    });
    return list.sort((a, b) => b.ts - a.ts).slice(0, 20);
  }, [wedding]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => n.ts > seenAt).length,
    [notifications, seenAt]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        const now = Date.now();
        setSeenAt(now);
        localStorage.setItem(SEEN_KEY, String(now));
      }
      return next;
    });
  };

  return (
    <header className="h-16 bg-white border-b border-gray-100 px-4 sm:px-6 flex items-center justify-between flex-shrink-0 shadow-soft">
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors -ml-1"
          aria-label="Abrir menu"
        >
          <Menu size={20} />
        </button>
        <div />
      </div>
      <div className="flex items-center gap-3">
        <div className="relative">
          <button
            ref={btnRef}
            onClick={toggle}
            className="relative p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
            aria-label="Notificações"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {open && (
            <div
              ref={panelRef}
              className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900">Notificações</p>
                <button onClick={() => setOpen(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400">
                  <X size={15} />
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="py-12 text-center">
                    <Bell size={28} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">Sem novidades por enquanto.</p>
                  </div>
                ) : (
                  notifications.map((n) => (
                    <div key={n.id} className="flex items-start gap-3 px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${n.type === 'gift' ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-600'}`}
                      >
                        {n.type === 'gift' ? <Gift size={15} /> : <Users size={15} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-800">
                          <span className="font-semibold">{n.name}</span>{' '}
                          {n.type === 'gift' ? (
                            <>presenteou: <span className="text-gray-600">{n.detail}</span></>
                          ) : (
                            <span className="text-gray-600">{n.detail}</span>
                          )}
                        </p>
                        {n.type === 'gift' && n.value > 0 && (
                          <p className="text-xs font-semibold text-emerald-600 mt-0.5">{formatCurrency(n.value)}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-rose-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
            {getInitials(user?.name || 'U')}
          </div>
          <div className="hidden md:block">
            <p className="text-sm font-medium text-gray-800 leading-tight">{user?.name || 'Usuário'}</p>
            <p className="text-xs text-gray-400 leading-tight">{user?.email}</p>
          </div>
        </div>
      </div>
    </header>
  );
}
